import { useEffect, useState } from "react";
import { Navigate, useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fechaSolo, fechaHora, money } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ChevronLeft, PackageOpen, Pencil, Plus, Trash2, X, Check } from "lucide-react";

import { useConfirm } from "@/context/ConfirmContext";
import FechaInput from "@/components/FechaInput";
const ESTADO_BADGE = {
  pendiente: "bg-amber-100 text-amber-800",
  autorizada: "bg-emerald-100 text-emerald-800",
  rechazada: "bg-red-100 text-red-700",
};
const ESTADO_LABEL = { pendiente: "Pendiente", autorizada: "Autorizada", rechazada: "Rechazada" };
const mkProducto = () => ({ nombre: "", color: "", talla: "", cantidad: 1, precio: "", _key: crypto.randomUUID() });

export default function SolicitudMuestraDetalle() {
  const { id } = useParams();
  const { user } = useAuth();
  const confirmar = useConfirm();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({ cliente_nombre: "", fecha_requerida: "", productos: [] });
  const [busy, setBusy] = useState(false);
  const [rechazando, setRechazando] = useState(false);
  const [motivo, setMotivo] = useState("");

  const load = () => api.get(`/solicitudes-muestra/${id}`).then((r) => setM(r.data)).catch(() => setM(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  if (m === false) return <Navigate to="/solicitudes" replace />;
  if (!m) return <div className="p-6 lg:p-8 max-w-3xl"><Skeleton className="h-96 rounded-xl" /></div>;

  const esCreador = user?.id === m.solicitante_id;
  const esAdmin = user?.es_admin;
  const puedeEditar = (esCreador && m.estado_solicitud === "pendiente") || esAdmin;
  const editado = m.updated_at && m.created_at && m.updated_at !== m.created_at;

  const iniciarEdicion = () => {
    setForm({
      cliente_nombre: m.cliente_nombre || "",
      fecha_requerida: m.fecha_requerida ? m.fecha_requerida.slice(0, 10) : "",
      productos: (m.productos || []).map((p) => ({
        nombre: p.nombre, color: p.color || "", talla: p.talla || "",
        cantidad: p.cantidad || 1, precio: p.precio == null ? "" : p.precio,
        _key: p.id || crypto.randomUUID(),
      })),
    });
    setEditando(true);
  };

  const setProd = (i, campo, val) => setForm((f) => ({ ...f, productos: f.productos.map((p, idx) => idx === i ? { ...p, [campo]: val } : p) }));
  const addProd = () => setForm((f) => ({ ...f, productos: [...f.productos, mkProducto()] }));
  const delProd = (i) => setForm((f) => ({ ...f, productos: f.productos.filter((_, idx) => idx !== i) }));

  const guardar = async () => {
    setBusy(true);
    try {
      const url = esAdmin ? `/solicitudes-muestra/${id}/admin` : `/solicitudes-muestra/${id}`;
      await api.put(url, {
        cliente_nombre: form.cliente_nombre,
        fecha_requerida: form.fecha_requerida || null,
        productos: form.productos.filter((p) => p.nombre.trim()).map((p) => ({
          nombre: p.nombre, color: p.color, talla: p.talla,
          cantidad: Number(p.cantidad) || 1, precio: p.precio === "" ? null : Number(p.precio),
        })),
      });
      toast.success("Solicitud actualizada");
      setEditando(false);
      window.dispatchEvent(new Event("solicitudes-changed"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const eliminar = async () => {
    if (!(await confirmar({ titulo: `¿Eliminar la solicitud ${m.folio}?`, mensaje: "Dejará de aparecer en los listados.", accion: "Eliminar solicitud", peligro: true }))) return;
    setBusy(true);
    try {
      await api.delete(`/solicitudes-muestra/${id}`);
      toast.success("Solicitud eliminada");
      window.dispatchEvent(new Event("solicitudes-changed"));
      navigate("/solicitudes");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const puedeAutorizar = user?.es_admin || user?.perms?.acciones?.autorizar_solicitudes;

  const autorizarSolicitud = async () => {
    setBusy(true);
    try {
      await api.post(`/solicitudes-muestra/${id}/autorizar`);
      toast.success("Solicitud autorizada");
      window.dispatchEvent(new Event("solicitudes-changed"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const confirmarRechazar = async () => {
    if (!motivo.trim()) return;
    setBusy(true);
    try {
      await api.post(`/solicitudes-muestra/${id}/rechazar`, { motivo });
      toast.success("Solicitud rechazada");
      setRechazando(false); setMotivo("");
      window.dispatchEvent(new Event("solicitudes-changed"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-3xl pb-16" data-testid="solicitud-muestra-detalle">
      <div className="flex items-center gap-1.5 text-sm text-slate-500 mb-4">
        <Link to="/solicitudes" className="inline-flex items-center gap-1 hover:text-slate-800 transition-colors"><ChevronLeft size={15} /> Solicitudes</Link>
        <span className="text-slate-300">/</span>
        <span className="font-medium text-slate-800">{m.folio}</span>
      </div>

      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-slate-900 text-white flex items-center justify-center"><PackageOpen size={18} /></div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">{m.folio}</h1>
          <span className={`text-xs px-2 py-1 rounded-full font-semibold ${ESTADO_BADGE[m.estado_solicitud] || "bg-slate-100 text-slate-600"}`} data-testid="detalle-estado">
            {ESTADO_LABEL[m.estado_solicitud] || m.estado_solicitud}
          </span>
        </div>
        {puedeAutorizar && m.estado_solicitud === "pendiente" && !editando && (
          <div className="flex items-center gap-2">
            <Button className="rounded-lg bg-emerald-600 hover:bg-emerald-700" onClick={autorizarSolicitud} disabled={busy} data-testid="detalle-autorizar-button"><Check size={15} className="mr-1.5" /> Autorizar</Button>
            <Button variant="outline" className="rounded-lg text-red-600 border-red-200 hover:bg-red-50" onClick={() => { setRechazando(true); setMotivo(""); }} disabled={busy} data-testid="detalle-rechazar-button"><X size={15} className="mr-1.5" /> Rechazar</Button>
          </div>
        )}
        {puedeEditar && !editando && (
          <div className="flex items-center gap-2">
            <Button className="rounded-lg" onClick={iniciarEdicion} data-testid="editar-solicitud-button"><Pencil size={15} className="mr-1.5" /> Editar solicitud</Button>
            {esAdmin && (
              <Button variant="outline" className="rounded-lg text-red-600 border-red-200 hover:bg-red-50" onClick={eliminar} disabled={busy} data-testid="eliminar-solicitud-button"><Trash2 size={15} className="mr-1.5" /> Eliminar</Button>
            )}
          </div>
        )}
        {editando && (
          <Button variant="outline" className="rounded-lg" onClick={() => setEditando(false)} data-testid="cancelar-edicion-button"><X size={15} className="mr-1.5" /> Cancelar</Button>
        )}
      </div>

      {m.estado_solicitud === "rechazada" && m.motivo_rechazo && (
        <Card className="border-red-200 bg-red-50 p-4 mb-6 text-sm text-red-700" data-testid="detalle-motivo-rechazo">
          <span className="font-semibold">Motivo del rechazo:</span> {m.motivo_rechazo}
        </Card>
      )}

      {m.comentario_creacion && (
        <Card className="border-slate-200 shadow-sm p-5 mb-6" data-testid="comentarios-card">
          <div className="text-xs text-slate-400 uppercase tracking-wide">Comentarios</div>
          <div className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap" data-testid="detalle-comentario-creacion">{m.comentario_creacion}</div>
        </Card>
      )}

      {!editando ? (
        <>
          <Card className="border-slate-200 shadow-sm p-5 mb-6">
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div><div className="text-xs text-slate-400 uppercase tracking-wide">Cliente</div><div className="font-semibold text-slate-800 mt-0.5" data-testid="detalle-cliente">{m.cliente_nombre}</div></div>
              <div><div className="text-xs text-slate-400 uppercase tracking-wide">Fecha requerida</div><div className="font-semibold text-slate-800 mt-0.5">{m.fecha_requerida ? fechaSolo(m.fecha_requerida) : "—"}</div></div>
              <div><div className="text-xs text-slate-400 uppercase tracking-wide">Creada por</div><div className="font-semibold text-slate-800 mt-0.5">{m.solicitante_nombre}</div></div>
              <div><div className="text-xs text-slate-400 uppercase tracking-wide">Fecha de creación</div><div className="font-semibold text-slate-800 mt-0.5">{fechaHora(m.created_at)}</div></div>
              {editado && (
                <div><div className="text-xs text-slate-400 uppercase tracking-wide">Última edición</div><div className="font-semibold text-slate-800 mt-0.5" data-testid="detalle-ultima-edicion">{fechaHora(m.updated_at)}</div></div>
              )}
            </div>
          </Card>

          <Card className="border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3.5 border-b border-slate-100"><h3 className="font-head font-bold tracking-tight">Productos</h3></div>
            <div className="overflow-x-auto thin-scroll">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-100 bg-slate-50/60">
                    <th className="px-5 py-2.5 font-semibold">Producto</th>
                    <th className="px-3 py-2.5 font-semibold">Color</th>
                    <th className="px-3 py-2.5 font-semibold">Talla</th>
                    <th className="px-3 py-2.5 font-semibold">Cant.</th>
                    <th className="px-3 py-2.5 font-semibold">Precio</th>
                  </tr>
                </thead>
                <tbody>
                  {m.productos.map((p) => (
                    <tr key={p.id} className="border-b border-slate-50" data-testid={`detalle-prod-${p.id}`}>
                      <td className="px-5 py-3 font-medium text-slate-800">{p.nombre}</td>
                      <td className="px-3 py-3 text-slate-600">{p.color || "—"}</td>
                      <td className="px-3 py-3 text-slate-600">{p.talla || "—"}</td>
                      <td className="px-3 py-3 text-slate-600">{p.cantidad}</td>
                      <td className="px-3 py-3 text-slate-600">{p.precio != null ? money(p.precio) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {esAdmin && m.admin_logs && m.admin_logs.length > 0 && (
            <Card className="border-slate-200 shadow-sm p-5 mt-6" data-testid="detalle-auditoria">
              <h3 className="font-head font-bold tracking-tight mb-3">Auditoría (Admin)</h3>
              <ul className="space-y-2 text-sm">
                {m.admin_logs.slice().reverse().map((l, i) => (
                  <li key={`${l.fecha}-${l.usuario_id}-${i}`} className="flex items-center gap-2 text-slate-600" data-testid={`auditoria-log-${i}`}>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${l.accion === "eliminada" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>{l.accion === "eliminada" ? "Eliminada" : "Editada"}</span>
                    <span className="font-medium text-slate-800">{l.usuario_nombre}</span>
                    <span className="text-slate-400">· {fechaHora(l.fecha)}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      ) : (
        <Card className="border-slate-200 shadow-sm p-5 space-y-5" data-testid="edicion-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Nombre del cliente *</Label><Input value={form.cliente_nombre} onChange={(e) => setForm({ ...form, cliente_nombre: e.target.value })} data-testid="edit-cliente-input" /></div>
            <div className="space-y-1.5"><Label>Fecha requerida</Label><FechaInput value={form.fecha_requerida} onChange={(e) => setForm({ ...form, fecha_requerida: e.target.value })} data-testid="edit-fecha-input" /></div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Productos</p>
              <Button type="button" size="sm" variant="outline" className="h-7" onClick={addProd} data-testid="edit-add-producto"><Plus size={14} className="mr-1" /> Agregar producto</Button>
            </div>
            <div className="space-y-2">
              {form.productos.map((p, i) => (
                <div key={p._key} className="grid grid-cols-12 gap-2 items-end" data-testid={`edit-producto-${i}`}>
                  <div className="col-span-4 space-y-1"><Label className="text-[11px]">Producto</Label><Input value={p.nombre} onChange={(e) => setProd(i, "nombre", e.target.value)} data-testid={`edit-prod-nombre-${i}`} /></div>
                  <div className="col-span-2 space-y-1"><Label className="text-[11px]">Color</Label><Input value={p.color} onChange={(e) => setProd(i, "color", e.target.value)} data-testid={`edit-prod-color-${i}`} /></div>
                  <div className="col-span-2 space-y-1"><Label className="text-[11px]">Talla</Label><Input value={p.talla} onChange={(e) => setProd(i, "talla", e.target.value)} data-testid={`edit-prod-talla-${i}`} /></div>
                  <div className="col-span-1 space-y-1"><Label className="text-[11px]">Cant.</Label><Input type="number" value={p.cantidad} onChange={(e) => setProd(i, "cantidad", e.target.value)} data-testid={`edit-prod-cantidad-${i}`} /></div>
                  <div className="col-span-2 space-y-1"><Label className="text-[11px]">Precio (MXN)</Label><Input type="number" value={p.precio} onChange={(e) => setProd(i, "precio", e.target.value)} placeholder="opc." data-testid={`edit-prod-precio-${i}`} /></div>
                  <div className="col-span-1 flex justify-center pb-1">
                    {form.productos.length > 1 && <button type="button" onClick={() => delProd(i)} className="text-slate-400 hover:text-red-600" data-testid={`edit-del-producto-${i}`}><Trash2 size={16} /></button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={guardar} disabled={busy || !form.cliente_nombre.trim() || !form.productos.some((p) => p.nombre.trim())} data-testid="edit-save-button">Guardar cambios</Button>
          </div>
        </Card>
      )}

      <Dialog open={rechazando} onOpenChange={(o) => !o && setRechazando(false)}>
        <DialogContent className="max-w-md" data-testid="detalle-rechazar-modal">
          <DialogHeader><DialogTitle>Rechazar solicitud</DialogTitle></DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Motivo <span className="text-red-500">*</span></Label>
            <Textarea rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Explica por qué se rechaza esta solicitud…" data-testid="detalle-rechazar-motivo" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRechazando(false)}>Cancelar</Button>
            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={confirmarRechazar} disabled={busy || !motivo.trim()} data-testid="detalle-rechazar-confirm">Rechazar solicitud</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
